///
/// EarthBrowser
///
/// Created by Matt Giger on 9/15/2026
/// Copyright (c) 2026 Geodata Labs
///

import { type Database } from "@/earthbrowser/database/Database"
import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { engineBridge } from "@/earthbrowser/engineBridge"
import { engineNotifier, type WeatherAlertNotification } from "@/earthbrowser/engineNotifications"
import { DatasetLayer, type DatasetDelta, type DatasetManifest, type DatasetSceneObject, type DatasetSyncConfig } from "../DatasetLayer"
import { AlertFeature } from "./AlertFeature"
import { AlertsTable, type AlertProperties, type AlertSeverity } from "./AlertsTable"

/** Raw Arrow row shape for the alerts feed (only fields we read) */
interface AlertRow {
	id: unknown
	source: unknown
	country: unknown
	event: unknown
	headline: string | null | undefined
	description: string | null | undefined
	instruction: string | null | undefined
	severity: unknown
	urgency: string | null | undefined
	certainty: string | null | undefined
	message_type: unknown
	category: string | null | undefined
	response_type: string | null | undefined
	area_desc: string | null | undefined
	geocodes: string | null | undefined
	sent: unknown
	effective: string | null | undefined
	onset: string | null | undefined
	expires: string | null | undefined
	ends: string | null | undefined
	sender_name: string | null | undefined
	web_url: string | null | undefined
	centroid_lat: unknown
	centroid_lon: unknown
	bbox_min_lon: unknown
	bbox_min_lat: unknown
	bbox_max_lon: unknown
	bbox_max_lat: unknown
	geometry: string | null | undefined
	ingested_at: unknown
}

const cValidSeverities: AlertSeverity[] = ["Extreme", "Severe", "Moderate", "Minor", "Unknown"]

function truncate(text: string, maxLength: number): string {
	return text.length > maxLength ? `${text.slice(0, maxLength - 1)}…` : text
}

export class AlertsDatasetLayer extends DatasetLayer<AlertProperties> {
	private unsubscribeRedux?: () => void

	constructor(context: EarthBrowserContext) {
		super(context, "alerts")
		this.subscribeToVisibility()
	}

	private subscribeToVisibility(): void {
		this.visible = engineBridge.getState()?.earthbrowser.visibleDatasets.includes("alert") ?? false
		this.unsubscribeRedux = engineBridge.subscribe(() => {
			const isVisible = engineBridge.getState()?.earthbrowser.visibleDatasets.includes("alert") ?? false
			if (isVisible !== this.visible) {
				this.visible = isVisible
			}
		})
	}

	override destroy(): void {
		this.unsubscribeRedux?.()
		super.destroy()
	}

	// ==================== Sync Configuration ====================

	public getSyncConfig(): DatasetSyncConfig {
		return {
			datasetId: "alerts",
			pollIntervalMs: 300_000, // 5 minutes, matching the publish cadence
			needsSync: true,
			supportsDelta: true,
			deltaOnlyIngest: true,
		}
	}

	public async createTable(db: Database): Promise<void> {
		await AlertsTable.create(db)
	}

	public async applyCheckpoint(
		manifest: DatasetManifest,
		downloadArrowRows: (url: string) => Promise<unknown[]>
	): Promise<void> {
		const shard = manifest.checkpoint.shards[0]
		if (!shard) return

		const url = this.resolveUrl(shard.url, shard.path)
		const rows = await downloadArrowRows(url)
		await AlertsTable.replaceAll(this.parseAlertRows(rows))
	}

	public async applyDelta(
		delta: DatasetDelta,
		downloadArrowRows: (url: string) => Promise<unknown[]>
	): Promise<void> {
		// Full active-alert snapshot each publish (delta-only protocol), so
		// replaceAll also handles alerts that expired or were cancelled
		const upsertsUrl = this.resolveUrl(delta.upserts.url, delta.upserts.path)
		const upsertRows = await downloadArrowRows(upsertsUrl)
		const alerts = this.parseAlertRows(upsertRows)
		await this.notifyNewAlerts(alerts)
		await AlertsTable.replaceAll(alerts)

		if (delta.deletes?.path) {
			const deletesUrl = this.resolveUrl(delta.deletes.url, delta.deletes.path)
			const deletesRows = await downloadArrowRows(deletesUrl)
			const deletes = (deletesRows as Array<{ id: string }>).map(r => String(r.id))
			if (deletes.length > 0) {
				await AlertsTable.deleteByIds(deletes)
			}
		}
	}

	/**
	 * Toast when a fresh sync carries new Extreme or Severe alerts, so
	 * someone watching the map hears about dangerous weather without
	 * scanning polygons. Skipped on first sync (everything would be "new")
	 * and while the alerts overlay is hidden. Only alerts with geometry
	 * toast, since the deep link needs a scene object to fly to.
	 */
	private async notifyNewAlerts(alerts: AlertProperties[]): Promise<void> {
		if (!this.visible) return

		const existingIds = await AlertsTable.getAllIds()
		if (existingIds.size === 0) return

		const updates: WeatherAlertNotification[] = []
		for (const alert of alerts) {
			if (existingIds.has(alert.id)) continue
			if (alert.severity !== "Extreme" && alert.severity !== "Severe") continue
			if (!alert.geometry) continue
			const area = alert.area_desc ? ` · ${truncate(alert.area_desc, 60)}` : ""
			updates.push({ id: alert.id, label: `${alert.event}${area}`, severity: alert.severity })
		}

		engineNotifier.weatherAlerts(updates)
	}

	private parseAlertRows(rows: unknown[]): AlertProperties[] {
		return (rows as AlertRow[]).map(r => ({
			id: String(r.id),
			source: String(r.source),
			country: String(r.country),
			event: String(r.event),
			headline: r.headline ? String(r.headline) : null,
			description: r.description ? String(r.description) : null,
			instruction: r.instruction ? String(r.instruction) : null,
			severity: this.parseSeverity(r.severity),
			urgency: r.urgency ? String(r.urgency) : null,
			certainty: r.certainty ? String(r.certainty) : null,
			message_type: String(r.message_type),
			category: r.category ? String(r.category) : null,
			response_type: r.response_type ? String(r.response_type) : null,
			area_desc: r.area_desc ? String(r.area_desc) : null,
			geocodes: r.geocodes ? String(r.geocodes) : null,
			sent: String(r.sent),
			effective: r.effective ? String(r.effective) : null,
			onset: r.onset ? String(r.onset) : null,
			expires: r.expires ? String(r.expires) : null,
			ends: r.ends ? String(r.ends) : null,
			sender_name: r.sender_name ? String(r.sender_name) : null,
			web_url: r.web_url ? String(r.web_url) : null,
			centroid_lat: r.centroid_lat === null || r.centroid_lat === undefined ? null : Number(r.centroid_lat),
			centroid_lon: r.centroid_lon === null || r.centroid_lon === undefined ? null : Number(r.centroid_lon),
			bbox_min_lon: r.bbox_min_lon === null || r.bbox_min_lon === undefined ? null : Number(r.bbox_min_lon),
			bbox_min_lat: r.bbox_min_lat === null || r.bbox_min_lat === undefined ? null : Number(r.bbox_min_lat),
			bbox_max_lon: r.bbox_max_lon === null || r.bbox_max_lon === undefined ? null : Number(r.bbox_max_lon),
			bbox_max_lat: r.bbox_max_lat === null || r.bbox_max_lat === undefined ? null : Number(r.bbox_max_lat),
			geometry: r.geometry ? String(r.geometry) : null,
			ingested_at: String(r.ingested_at),
		}))
	}

	private parseSeverity(value: unknown): AlertSeverity {
		const severity = String(value) as AlertSeverity
		return cValidSeverities.includes(severity) ? severity : "Unknown"
	}

	// ==================== Scene Objects ====================

	public async queryFeatures(db: Database): Promise<AlertProperties[]> {
		// Only alerts with polygons render in the scene; zone-code-only
		// alerts (MeteoAlarm, zone-based NWS products) are data-only for
		// now. Cap the scene at the 1000 most recent in case of an outbreak.
		const rows = await db.exec(`
			select *
			from weather_alerts
			where geometry is not null
			order by sent desc
			limit 1000
		`) as AlertProperties[]
		return rows
	}

	public override async loadSceneObjectById(id: string): Promise<DatasetSceneObject<AlertProperties> | undefined> {
		const existing = this.getSceneObject(id)
		if (existing) {
			return existing
		}

		const alert = await AlertsTable.getById(id)
		if (!alert) return undefined
		this.addSceneObject(alert)
		return this.sceneObjects.get(id)
	}

	public createSceneObject(feature: AlertProperties): DatasetSceneObject<AlertProperties> | undefined {
		if (!feature.geometry) return undefined
		return new AlertFeature(this.context, feature)
	}
}
